import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { PropertyStatus } from '../types/api';

interface StatusSelectProps {
  value: PropertyStatus;
  onChange: (status: PropertyStatus) => void;
  onExclude?: (reason: string) => void;
  disabled?: boolean;
  compact?: boolean;
}

const STATUS_COLORS: Record<PropertyStatus, string> = {
  new: '#1A9E8F',
  interested: '#22c55e',
  viewing: '#f59e0b',
  applied: '#8b5cf6',
  rejected: '#ef4444',
  excluded: '#6b7280',
  rented: '#10b981',
};

const STATUS_VALUES: PropertyStatus[] = ['new', 'interested', 'viewing', 'applied', 'rejected', 'excluded', 'rented'];

const EXCLUDE_REASON_KEYS = ['tooExpensive', 'wrongArea', 'tooSmall', 'badReviews', 'other'] as const;

export function StatusSelect({ value, onChange, onExclude, disabled, compact }: StatusSelectProps) {
  const { t } = useTranslation();
  const [showReasons, setShowReasons] = useState(false);
  const currentColor = STATUS_COLORS[value];

  // Hide reason picker when status changes away from excluded
  useEffect(() => {
    if (value !== 'excluded') {
      setShowReasons(false);
    }
  }, [value]);

  const handleChange = (newValue: string) => {
    const status = newValue as PropertyStatus;
    onChange(status);
    if (status === 'excluded' && onExclude) {
      setShowReasons(true);
    }
  };

  const handleReasonSelect = (reason: string) => {
    setShowReasons(false);
    onExclude?.(reason);
  };

  return (
    <div className={`status-select${compact ? ' status-select-compact' : ''}`}>
      {!compact && <label className="input-label">{t('app.statusSelect.label')}</label>}
      <div className="status-select-row">
        <select
          className="status-native-select"
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          disabled={disabled}
          style={{
            color: currentColor,
            borderColor: currentColor,
            backgroundColor: currentColor ? `${currentColor}15` : undefined,
          }}
        >
          {STATUS_VALUES.map((statusVal) => (
            <option key={statusVal} value={statusVal}>
              {t(`status.${statusVal}`)}
            </option>
          ))}
        </select>
      </div>
      {showReasons && (
        <div className="exclude-reasons-inline">
          <span className="exclude-reasons-label">{t('app.statusSelect.reasonPrefix')}</span>
          <select
            className="exclude-reason-select"
            defaultValue=""
            onChange={(e) => {
              if (e.target.value) handleReasonSelect(e.target.value);
            }}
            autoFocus
          >
            <option value="" disabled>{t('app.statusSelect.reasonPlaceholder')}</option>
            {EXCLUDE_REASON_KEYS.map((key) => (
              <option key={key} value={t(`app.statusSelect.reasons.${key}`)}>{t(`app.statusSelect.reasons.${key}`)}</option>
            ))}
          </select>
          <button
            className="exclude-reasons-cancel"
            onClick={() => setShowReasons(false)}
          >
            {t('common.cancel')}
          </button>
        </div>
      )}
    </div>
  );
}
